import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Phone } from 'lucide-react';
import { Label } from '../../../../components/ui/label';

interface PhoneNumberFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  defaultCountry?: string;
  className?: string;
}

const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  placeholder = "Enter phone number",
  defaultCountry = "US",
  className = ""
}) => {
  const [isValid, setIsValid] = useState<boolean>(true);

  // Custom styles to match your existing form styling
  const customStyles = `
    .PhoneInput {
      position: relative;
    }
    
    .PhoneInputInput {
      width: 100%;
      height: 2.5rem;
      padding: 0.5rem 0.75rem 0.5rem 3rem;
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
      background-color: transparent;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      text-align: left;
    }
    
    .PhoneInputInput:focus {
      outline: none;
      border-color: #EB1C37;
      box-shadow: 0 0 0 1px #EB1C37;
      background-color: transparent;
    }
    
    .PhoneInputInput.has-value {
      background-color: transparent;
      border-color: #d1d5db;
    }
    
    .PhoneInputInput:focus {
      outline: none;
      border-color: #EB1C37;
      box-shadow: 0 0 0 1px #EB1C37;
    }
    
    .PhoneInputInput:disabled {
      background-color: #f9fafb;
      color: #6b7280;
      cursor: not-allowed;
      border-color: #d1d5db;
    }
    
    .PhoneInputInput.error {
      border-color: #ef4444;
    }
    
    .PhoneInputCountry {
      position: absolute;
      left: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
    }
    
    .PhoneInputCountrySelect {
      background: transparent;
      border: none;
      font-size: 0.875rem;
      color: #374151;
      cursor: pointer;
      padding: 0;
      margin: 0;
      min-width: 60px;
    }
    
    .PhoneInputCountrySelect:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
    
    .PhoneInputCountryIcon {
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 0.125rem;
    }
    
    .PhoneInputCountrySelectArrow {
      display: none;
    }
    
    .PhoneInputCountrySelectArrow--border {
      display: none;
    }
    
    /* Fix for country code spacing and alignment */
    .PhoneInputCountrySelect--border {
      border: none !important;
    }
    
    .PhoneInputCountrySelect--border .PhoneInputCountrySelectArrow {
      display: none !important;
    }
    
    /* Ensure proper text alignment */
    .PhoneInputInput input {
      text-align: left !important;
      padding-left: 0 !important;
    }
    
    /* Remove any extra spacing around country code */
    .PhoneInputCountrySelect {
      margin-right: 0.5rem !important;
    }
    
    /* Ensure proper spacing between country code and phone number */
    .PhoneInputInput {
      padding-left: 3.5rem !important;
      width: 100% !important;
      box-sizing: border-box !important;
    }
    
    /* Fix alignment for the phone number input */
    .PhoneInputInput input {
      border: none !important;
      background: transparent !important;
      outline: none !important;
      box-shadow: none !important;
    }
    
    /* Ensure country code is properly positioned */
    .PhoneInputCountry {
      left: 0.75rem !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
    }
    
    /* Style for country names display */
    .PhoneInputCountrySelect {
      font-weight: 500 !important;
      color: #374151 !important;
    }
    
    /* Ensure proper spacing for country names */
    .PhoneInputCountrySelect option {
      font-size: 0.875rem !important;
      padding: 0.25rem 0.5rem !important;
    }
  `;

  // Validate phone number
  const validatePhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) {
      return required ? 'Phone number is required' : '';
    }
    
    // Basic validation - you can enhance this based on your requirements
    if (phoneNumber.length < 10) {
      return 'Phone number must be at least 10 digits';
    }
    
    return '';
  };

  const handlePhoneChange = (newValue: string | undefined) => {
    const phoneValue = newValue || '';
    onChange(phoneValue);
    
    // Validate the new value
    const validationError = validatePhoneNumber(phoneValue);
    setIsValid(!validationError);
  };

  // Add custom styles to document head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = customStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      <PhoneInput
        international
        defaultCountry={defaultCountry as any}
        value={value}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`PhoneInputInput ${error ? 'error' : ''} ${disabled ? 'disabled' : ''} ${value ? 'has-value' : ''}`}
        countrySelectProps={{
          className: 'PhoneInputCountrySelect',
          disabled: disabled,
          unicodeFlags: true
        }}
        withCountryCallingCode={true}
        addInternationalOption={true}
      />
      
      {/* Error message */}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};

export default PhoneNumberField; 